import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { DonationTypeToggleComponent } from '../../shared/form-controls/donation-type-toggle.component';
import {
  donationTypeAllowed,
  donationTypeErrorMessage,
} from '../../donation/donation-type-validation';

import { TranslateModule } from '@ngx-translate/core';

import { PriceEnvelope, PriceRequest } from '../../../types/stripe-models';
import { selectSelectedProductId } from '../../../state/campaing/selectors';

// 🔹 NUEVO: planes centralizados
import { DONATION_PLANS, PlanId } from '../../../types/donation-plans';

type DonationType = 'monthly' | 'one-time';
type PriceInput = PriceEnvelope & { donationType: DonationType };

@Component({
    selector: 'don-pop-up-form',
    imports: [CommonModule, ReactiveFormsModule, DonationTypeToggleComponent, TranslateModule],
    templateUrl: './pop-up-form.component.html',
    styleUrls: ['./pop-up-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PopUpFormComponent implements OnChanges {
  private fb = inject(FormBuilder);
  private store = inject(Store);

  /** Igual que en PriceForm: alimenta el form desde el store */
  @Input() price!: PriceInput;

  /** Habilita/inhabilita 'monthly' según la campaña */
  @Input() canAcceptRecurring = false;

  /** Emite EXACTAMENTE un PriceEnvelope, como PriceFormComponent */
  @Output() submitted = new EventEmitter<PriceEnvelope>();

  // 🔹 NUEVO: planes centralizados (público para el template)
  public readonly plans = inject(DONATION_PLANS);

  // 🔹 NUEVO: IDs deshabilitados para el toggle cuando no se permite recurring
  get disabledPlanIds(): PlanId[] {
    if (this.canAcceptRecurring) return [];
    return this.plans.filter((p) => !!p.recurring).map((p) => p.id) as PlanId[];
  }

  form = this.fb.group({
    donationType: this.fb.nonNullable.control<DonationType>('monthly', {
      validators: [Validators.required],
    }),
    /** Monto en unidades MAYORES (ej. 12.34 USD) */
    unitAmount: this.fb.control<number | null>(null, {
      validators: [Validators.required, Validators.min(5)],
    }),
    /** Fijamos USD pero lo mantenemos en el form para convertir a minor units */
    currency: this.fb.nonNullable.control<string>('USD', {
      validators: [Validators.required],
    }),
    /** No lo mostramos, pero mantenemos compatibilidad */
    nickname: this.fb.nonNullable.control<string>('Default Price', {
      validators: [Validators.required],
    }),
  });

  /* ===== helpers ===== */
  getCurrencyDecimals(curUpper: string): number {
    const ZERO_DEC = new Set(['JPY', 'KRW', 'CLP']);
    return ZERO_DEC.has(curUpper) ? 0 : 2;
  }
  toMinorUnits(amountMajor: number, curUpper: string): string {
    const factor = Math.pow(10, this.getCurrencyDecimals(curUpper));
    return Math.round((amountMajor ?? 0) * factor).toString();
  }

  ngOnInit(): void {
    const defaultPlanId = (this.plans.find(p => p.default)?.id as DonationType) ?? 'one-time';

    this.form.get('donationType')!.setValue(defaultPlanId, { emitEvent: false });
  }

  /* ===== binding de inputs ===== */
  ngOnChanges(changes: SimpleChanges): void {
    // Validador de tipo según la campaña (mantengo tu lógica)
    const typeCtrl = this.form.get('donationType');
    if (typeCtrl) {
      typeCtrl.setValidators([
        Validators.required,
        donationTypeAllowed(this.canAcceptRecurring),
      ]);
      typeCtrl.updateValueAndValidity({ emitEvent: false });
    }

    // Hidratar desde 'price' si llega algo del store
    const p = changes['price']?.currentValue as PriceInput | undefined;
    if (!p) return;

    const curUpper = (p.data?.currency ?? 'USD').toUpperCase();
    const patch: Partial<{
      donationType: DonationType;
      unitAmount: number;
      nickname: string;
      currency: string;
    }> = {
      nickname: p.data?.nickname ?? 'Default Price',
      currency: curUpper,
    };

    // `unitAmount` del envelope viene en MINOR units (string). Lo pasamos a mayor.
    if (typeof p.data?.unitAmount === 'string') {
      const major =
        Number(p.data.unitAmount) /
        Math.pow(10, this.getCurrencyDecimals(curUpper));
      if (!isNaN(major) && isFinite(major)) patch.unitAmount = major;
    }

    // Mapear el tipo entrante: en UI usamos 'monthly' para "recurring".
    const incoming = p.donationType; // 'monthly' | 'one-time'
    if (incoming) patch.donationType = incoming;

    this.form.patchValue(patch, { emitEvent: false });
  }

  /* ===== UI helpers ===== */
  get donationTypeError(): string {
    const ctrl = this.form.get('donationType');
    return donationTypeErrorMessage(ctrl?.errors);
  }

  /* ===== submit ===== */
  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue() as {
      donationType: DonationType;
      unitAmount: number;
      currency: string;
      nickname: string;
    };

    const currencyUpper = (v.currency || 'USD').toUpperCase();
    const currencyLower = currencyUpper.toLowerCase();
    const unitAmountMinor = this.toMinorUnits(v.unitAmount, currencyUpper);

    this.store
      .select(selectSelectedProductId)
      .pipe(take(1))
      .subscribe((pid) => {
        const data: PriceRequest = {
          action: 'Create',
          productId: pid ?? undefined,
          currency: currencyLower,
          unitAmount: unitAmountMinor,
          nickname: v.nickname,
          ...(v.donationType === 'monthly'
            ? { recurring: { interval: 'month' } }
            : {}),
        };

        const envelope: PriceEnvelope = { type: 'Price', data };
        this.submitted.emit(envelope);
      });
  }
}
